import { Archive, ArchiveRestore, CircleAlert, CircleCheck, Pencil, RefreshCw, Save, Tags, X } from 'lucide-react'

import { Fragment } from 'react'

import { QueryState } from '@krp/sale-ui'
import { Button, CheckboxField, InfoTip, SelectField, StatusMessage, TextField } from '@krp/ui-kit'

import type { ProductDraft } from '../model/productForm'
import type { OwnProductCardActions, OwnProductCardVm } from '../model/types'
import { CommandError, Problem, TextAreaField } from './parts'
import './own-nomenclature.css'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

const HELP = {
  vat: 'Ставка попадёт в чек как есть. Без ставки товар не продать — умолчаний нет.',
  subject: 'Что продаётся по закону о кассах (тег 1212): товар, услуга, маркированный товар…',
  method: 'Как рассчитывается покупатель (тег 1214): обычно «Полный расчёт».',
  unit: 'Код единицы по ОКЕИ: 796 — штука, 166 — килограмм. Имя — как его увидит кассир.',
  weight: 'Весовой товар продаётся на вес, штучный — штуками. Не определён — товар не продать.',
  marked: 'Маркированный товар при продаже требует код маркировки с упаковки.',
}

/** Поля формы товара: и «Новый товар», и правка карточки — одна форма. */
function ProductForm({ vm, actions }: { vm: OwnProductCardVm; actions: OwnProductCardActions }) {
  const draft = vm.draft
  const change = (patch: Partial<ProductDraft>) => actions.draftChanged({ ...draft, ...patch })
  return (
    <div className="own-form">
      <div className="own-form__wide">
        <TextField label="Наименование" value={draft.name} onChange={(name) => change({ name })} />
      </div>
      <div className="own-form__wide">
        <TextField label="Полное наименование" value={draft.fullName} onChange={(fullName) => change({ fullName })} />
      </div>
      <TextField label="Артикул" value={draft.sku} onChange={(sku) => change({ sku })} />
      <SelectField label="Группа" value={draft.groupId} options={vm.groupOptions} onChange={(groupId) => change({ groupId })} />
      <TextField label="Код единицы (ОКЕИ)" value={draft.unitCode} onChange={(unitCode) => change({ unitCode })} />
      <TextField label="Единица" value={draft.unitName} onChange={(unitName) => change({ unitName })} />
      <p className="own-muted own-form__wide">{HELP.unit}</p>
      <div className="own-row own-form__wide">
        <SelectField
          label="Весовой признак"
          value={draft.isWeighted}
          options={vm.weightOptions}
          onChange={(isWeighted) => change({ isWeighted: isWeighted === 'true' || isWeighted === 'false' ? isWeighted : '' })}
        />
        <InfoTip label="Весовой признак">{HELP.weight}</InfoTip>
        <CheckboxField label="Маркированный товар" checked={draft.isMarked} onChange={(isMarked) => change({ isMarked })} />
        <InfoTip label="Маркированный товар">{HELP.marked}</InfoTip>
      </div>
      <TextAreaField
        label="Штрихкоды"
        value={draft.barcodes}
        onChange={(barcodes) => change({ barcodes })}
        hint="По одному в строке. Штрихкод, занятый другим товаром этой базы, служба не примет."
      />
      <h3 className="own-form__wide">Для чека</h3>
      <div className="own-row own-form__wide">
        <SelectField label="Ставка НДС" value={draft.vatRate} options={vm.vatOptions} onChange={(vatRate) => change({ vatRate })} />
        <InfoTip label="Ставка НДС">{HELP.vat}</InfoTip>
      </div>
      <div className="own-row">
        <SelectField
          label="Предмет расчёта"
          value={draft.paymentSubject}
          options={vm.subjectOptions}
          onChange={(paymentSubject) => change({ paymentSubject })}
        />
        <InfoTip label="Предмет расчёта">{HELP.subject}</InfoTip>
      </div>
      <div className="own-row">
        <SelectField
          label="Способ расчёта"
          value={draft.paymentMethod}
          options={vm.methodOptions}
          onChange={(paymentMethod) => change({ paymentMethod })}
        />
        <InfoTip label="Способ расчёта">{HELP.method}</InfoTip>
      </div>
    </div>
  )
}

/**
 * Карточка товара своей базы: поля, кассовые реквизиты, цены на сегодня; правка, архив и возврат из архива — с версией,
 * которую видел человек. Изменили раньше — отказ службы показывается дословно, «Перечитать» берёт свежую карточку.
 * «Новый товар» — та же форма; ставка, предмет и способ расчёта в нём обязательны.
 */
export function OwnProductCardScreen({ vm, actions }: { vm: OwnProductCardVm; actions: OwnProductCardActions }) {
  const write = vm.writeDisabledReason === null
  const editing = vm.mode !== 'view'
  return (
    <section className="own-screen" aria-labelledby="own-product-title">
      <header className="own-toolbar">
        <h2 id="own-product-title">{vm.title}</h2>
        {vm.mode === 'view' && vm.query.status === 'ready' && (
          <>
            <Button
              variant="primary"
              icon={<Pencil size={ICON_SIZE} aria-hidden="true" />}
              disabled={!write || vm.archived}
              onClick={actions.edit}
            >
              Изменить
            </Button>
            {vm.archived ? (
              <Button icon={<ArchiveRestore size={ICON_SIZE} aria-hidden="true" />} busy={vm.saving} disabled={!write} onClick={actions.restore}>
                Вернуть из архива
              </Button>
            ) : (
              <Button icon={<Archive size={ICON_SIZE} aria-hidden="true" />} busy={vm.saving} disabled={!write} onClick={actions.archive}>
                В архив
              </Button>
            )}
            {vm.canSeePrices && (
              <Button variant="ghost" icon={<Tags size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openPrices}>
                Цены товара
              </Button>
            )}
          </>
        )}
        {vm.mode !== 'create' && (
          <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
            Перечитать
          </Button>
        )}
      </header>

      {vm.writeDisabledReason !== null && (
        <p className="own-muted" role="note">
          Правка выключена. {vm.writeDisabledReason}
        </p>
      )}
      {vm.archived && (
        <p className="own-problem" role="note">
          <Archive size={ICON_SIZE_SMALL} aria-hidden="true" />
          Товар в архиве: его не продать и не изменить, пока не вернёте из архива.
        </p>
      )}
      {vm.done !== null && (
        <p className="own-problem" role="status">
          <CircleCheck size={ICON_SIZE_SMALL} aria-hidden="true" />
          {vm.done}
        </p>
      )}
      {vm.error !== null && <CommandError title="Не записано" error={vm.error} />}

      {vm.mode !== 'create' && <QueryState query={vm.query} emptyText="Товара нет" onRetry={actions.reload} what="Карточка" />}

      {vm.mode === 'view' && vm.query.status === 'ready' && (
        <>
          {vm.saleGaps.length > 0 && (
            <StatusMessage
              kind="forbidden"
              title="Этот товар пока не продать"
              text={`Не указано: ${vm.saleGaps.join(', ')}. Заполните в «Изменить».`}
            />
          )}
          <dl className="own-facts">
            {vm.facts.map((fact) => (
              <Fragment key={fact.label}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </Fragment>
            ))}
          </dl>
          <p className="own-muted">{vm.versionText}</p>
          <section className="own-section" aria-labelledby="own-product-prices-title">
            <h3 id="own-product-prices-title">Цены на {vm.pricesOnText}</h3>
            {vm.prices.length === 0 ? (
              <p className="own-muted">
                <CircleAlert size={ICON_SIZE_SMALL} aria-hidden="true" /> Цены на эту дату не заданы ни по одному виду цены.
              </p>
            ) : (
              <table className="own-table">
                <thead>
                  <tr>
                    <th>Вид цены</th>
                    <th data-numeric="true">Цена</th>
                    <th>Действует</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.prices.map((price) => (
                    <tr key={price.key}>
                      <td>{price.name}</td>
                      <td data-numeric="true">{price.priceText}</td>
                      <td>{price.fromText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {editing && (vm.mode === 'create' || vm.query.status === 'ready') && (
        <section className="own-section" aria-label={vm.mode === 'create' ? 'Новый товар' : 'Правка товара'}>
          <ProductForm vm={vm} actions={actions} />
          <Problem text={vm.problem} />
          <div className="own-row">
            <Button
              variant="primary"
              icon={<Save size={ICON_SIZE} aria-hidden="true" />}
              busy={vm.saving}
              disabled={!write || vm.problem !== null}
              onClick={actions.save}
            >
              {vm.mode === 'create' ? 'Создать товар' : 'Сохранить'}
            </Button>
            <Button variant="ghost" icon={<X size={ICON_SIZE} aria-hidden="true" />} disabled={vm.saving} onClick={vm.mode === 'create' ? actions.close : actions.cancel}>
              {vm.mode === 'create' ? 'Закрыть' : 'Отменить правку'}
            </Button>
          </div>
        </section>
      )}
    </section>
  )
}
